/**
 * NewCommunicationForm — Unified form to create a chart communication
 *
 * Replaces separate Telephone Note / Log Note / Nursing Note / Team Message forms.
 * Creates a conversation that appears in:
 *   - Patient Chart → Notes → Communications
 *   - My Day → Messages (for recipients)
 */
import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Phone, ClipboardList, Heart, MessageSquare, Stethoscope,
  X, Send, Plus, AlertCircle, User, Check, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { ORIGIN_CONFIG, PRIORITY_CONFIG } from './ChartCommunicationThread';

const ORIGIN_TYPES = [
  { key: 'phone',   label: 'Phone Call',   icon: Phone,         hint: 'Incoming or outgoing phone call' },
  { key: 'log',     label: 'Log Note',     icon: ClipboardList, hint: 'Record what happened — no reply needed' },
  { key: 'nursing', label: 'Nursing Note', icon: Heart,         hint: 'Nursing observation, admin, procedure' },
  { key: 'team',    label: 'Team Message', icon: MessageSquare, hint: 'Send to provider, nurse, or front desk' },
];

function RoleBadge({ role }) {
  const colors = {
    provider: 'bg-blue-100 text-blue-700',
    nurse: 'bg-teal-100 text-teal-700',
    front_desk: 'bg-purple-100 text-purple-700',
    office_manager: 'bg-orange-100 text-orange-700',
  };
  return <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded ${colors[role] || 'bg-gray-100 text-gray-600'}`}>{role?.replace(/_/g, ' ')}</span>;
}

export function NewCommunicationForm({ patientId, patientName, onCreated, onClose }) {
  const { user } = useAuth();
  const [originTag, setOriginTag] = useState('');
  const [priority, setPriority] = useState('routine');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState([]); // [{ id, name, role }]
  const [staffList, setStaffList] = useState([]);
  const [showStaffPicker, setShowStaffPicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load staff list + pre-fill from provider-nurse mapping
  useEffect(() => {
    api.get('/conversations/staff/recipients').then(r => {
      const staff = r.data.staff || [];
      setStaffList(staff);
      // Auto-select primary mapped nurses for team messages
      const mapped = staff.filter(s => s.is_primary_mapped);
      if (mapped.length > 0 && originTag === 'team') {
        setRecipients(mapped.slice(0, 1)); // auto-add primary nurse
      }
    }).catch(() => {});
  }, [originTag]);

  const toggleRecipient = (staff) => {
    setRecipients(prev =>
      prev.find(r => r.id === staff.id)
        ? prev.filter(r => r.id !== staff.id)
        : [...prev, staff]
    );
  };

  const isLogOnly = originTag === 'log';
  const canNotify = !isLogOnly && recipients.length > 0;

  const handleSubmit = async (notifyTeam) => {
    if (!originTag) { toast.error('Select a note type'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim()) { toast.error('Note body is required'); return; }
    if (notifyTeam && recipients.length === 0) { toast.error('Add at least one recipient to notify'); return; }

    setSubmitting(true);
    try {
      await api.post('/conversations', {
        patient_id: patientId,
        title: subject.trim(),
        body: body.trim(),
        origin_tag: originTag,
        priority,
        recipient_ids: notifyTeam ? recipients.map(r => r.id) : [],
        conversation_type: originTag === 'phone' ? 'patient_question' : originTag === 'nursing' ? 'nurse_question' : 'general',
      });
      toast.success(notifyTeam && recipients.length > 0
        ? `Logged in chart + sent to ${recipients.map(r => r.name).join(', ')}`
        : 'Logged in chart');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create note');
    } finally {
      setSubmitting(false);
    }
  };

  const activeType = ORIGIN_TYPES.find(t => t.key === originTag);

  return (
    <div className="rounded-xl border bg-card p-4 space-y-4 animate-in fade-in-0 slide-in-from-top-2 duration-150"
      data-testid="new-communication-form">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          New Chart Communication
        </h3>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded hover:bg-accent text-muted-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground -mt-2">
        For patient: <span className="font-medium text-foreground">{patientName}</span>
      </p>

      {/* Step 1: Origin tag */}
      {!originTag ? (
        <div>
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            What type of note?
          </p>
          <div className="grid grid-cols-2 gap-2">
            {ORIGIN_TYPES.map(t => (
              <button key={t.key}
                onClick={() => setOriginTag(t.key)}
                className="text-left p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all group"
                data-testid={`origin-${t.key}`}>
                <t.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary mb-1.5 transition-colors" />
                <span className="text-xs font-semibold text-foreground block">{t.label}</span>
                <span className="text-[9px] text-muted-foreground">{t.hint}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Type + priority row */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOriginTag('')}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium border ${ORIGIN_CONFIG[originTag]?.color}`}
              data-testid="change-origin">
              {activeType && <activeType.icon className="w-3 h-3" />}
              {activeType?.label}
              <X className="w-2.5 h-2.5 ml-0.5 opacity-60" />
            </button>
            <div className="flex gap-1">
              {Object.entries(PRIORITY_CONFIG).map(([k, cfg]) => (
                <button key={k}
                  onClick={() => setPriority(k)}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border transition-all ${
                    priority === k ? cfg.color + ' ring-1 ring-offset-1 ring-current/30' : 'bg-muted text-muted-foreground border-transparent hover:border-border'
                  }`}
                  data-testid={`priority-${k}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder={
                originTag === 'phone' ? 'e.g. Patient called re: medication refill' :
                originTag === 'log' ? 'e.g. Prior auth approved for MRI' :
                originTag === 'nursing' ? 'e.g. B12 injection administered' :
                'e.g. Follow-up on lab results'
              }
              className="w-full mt-1 text-sm border rounded-lg px-3 py-2 bg-background focus:ring-1 focus:ring-primary/30"
              data-testid="comm-subject"
              autoFocus
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Note</label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Document what happened, instructions given, information received..."
              rows={3}
              className="mt-1 text-sm resize-none"
              data-testid="comm-body"
            />
          </div>

          {/* Recipients (hidden for log notes) */}
          {!isLogOnly && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Notify team (optional)
                </label>
                <button
                  onClick={() => setShowStaffPicker(p => !p)}
                  className="text-[10px] text-primary hover:text-primary/80 flex items-center gap-0.5"
                  data-testid="add-recipient-btn">
                  <Plus className="w-3 h-3" /> Add person
                </button>
              </div>
              {/* Selected recipients */}
              {recipients.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {recipients.map(r => (
                    <span key={r.id}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium">
                      {r.name}
                      <RoleBadge role={r.role} />
                      <button onClick={() => toggleRecipient(r)} className="hover:text-red-500 ml-0.5">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {/* Staff picker */}
              {showStaffPicker && (
                <div className="border rounded-xl p-2 space-y-0.5 max-h-44 overflow-y-auto bg-card animate-in fade-in-0 duration-100">
                  {staffList.map(s => {
                    const selected = !!recipients.find(r => r.id === s.id);
                    return (
                      <button key={s.id}
                        onClick={() => toggleRecipient(s)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] hover:bg-accent transition-colors ${selected ? 'bg-primary/5' : ''}`}
                        data-testid={`staff-${s.id}`}>
                        <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-[8px] font-bold shrink-0">
                          {s.name.charAt(0)}
                        </div>
                        <div className="flex-1 text-left">
                          <span className="font-medium">{s.name}</span>
                          {s.is_primary_mapped && <span className="ml-1 text-[8px] text-violet-600">mapped</span>}
                        </div>
                        <RoleBadge role={s.role} />
                        {selected && <Check className="w-3 h-3 text-primary" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-1 border-t">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-xs gap-1.5"
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              data-testid="save-to-chart-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
              Save to Chart
            </Button>
            {!isLogOnly && (
              <Button
                size="sm"
                className="flex-1 text-xs gap-1.5"
                onClick={() => handleSubmit(true)}
                disabled={submitting || recipients.length === 0}
                title={recipients.length === 0 ? 'Add at least one recipient' : ''}
                data-testid="save-and-notify-btn">
                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Save & Notify
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
